import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { constants } from '../../../assets/constants/constants';
import { AppContext } from '../../../context/AppProvider';
import useSearchhook from '../../../hooks/useSearchhook';
import { librarySearch } from '../../../lib/utils';
import Search from '../../common/Search';
import BookSection from './BookSection';
import BullentinSection from './BullentinSection';
import DailyReminderSection from './DailyReminderSection';
import DocumentSection from './DocumentSection';
import PodcastSection from './PodcastSection';
import RecommendedSection from './RecommendedSection';
import VideoSection from './VideoSection';
import useGetEnrolled from '../../../hooks/useGetEnrolled';
import PageLoader from "../../common/loader/PageLoader";

type LibrarySectionIdsType = {
  [key: string]: string;
};

type OtherSectionType = {
  librarySectionsIds: LibrarySectionIdsType;
  addBookMark?: (id: string) => void;
  deleteBookMark?: (id: string) => void;
  loading: boolean;
};

const sections = [
  {
    name: 'videos',
    component: VideoSection,
    dataKey: 'Videos',
    catalogId: constants?.VIDEOS,
  },
  {
    name: 'Bulletins',
    component: BullentinSection,
    dataKey: 'Bulletins',
    catalogId: constants.BULLETINS,
  },
  {
    name: 'Documents',
    component: DocumentSection,
    dataKey: 'Documents',
    catalogId: constants.DOCUMENTS,
  },
  {
    name: 'Podcast',
    component: PodcastSection,
    dataKey: 'Podcast',
    catalogId: constants.PODCAST,
  },
  {
    name: "Books",
    component: BookSection,
    dataKey: 'Books',
    catalogId: constants.BOOKS,
  },
];

const renderSection = (
  section: { component: any; dataKey: string; catalogId: string | null },
  state: any,
  keyword: string,
  addBookMark: (id: string) => void,
  deleteBookMark: (id: string) => void,
  librarySectionsIds: LibrarySectionIdsType,
  tagsData:any
) => {
  const { component: Component, dataKey, catalogId } = section;
  const data = keyword ? state?.searchLibraryData?.[dataKey] : state?.libraryData[dataKey];
  if (keyword && !data?.length) return null;
  return (
    <Component
      key={dataKey}
      {...{ [dataKey.toLowerCase()]: data }}
      addBookMark={addBookMark}
      deleteBookMark={deleteBookMark}
      catalogId={catalogId ? librarySectionsIds[catalogId] : undefined}
      tagsData={tagsData}
    />
  );
};

const LibrarySubSections = ({
  librarySectionsIds,
  addBookMark = () => { },
  deleteBookMark = () => { },
  loading,
}: OtherSectionType) => {
  const { keyword, onhandleSearch, onSearchClick } = useSearchhook();
  const { state, dispatch } = useContext(AppContext);
  const { t } = useTranslation();
  const { tagsData } = useGetEnrolled()
  useEffect(() => {
    if (keyword) {
      const libraryData: Record<string, any> = {};
      Object.keys(state?.libraryData).forEach((key: any) => {
        const searchResult = librarySearch(keyword, state?.libraryData[key]);
        libraryData[key] = searchResult;
      });
      dispatch({ type: 'SEARCH_LIBRARY_DATA', payload: libraryData });
    }
  }, [keyword,deleteBookMark,addBookMark]);

  const isEmpty =
    keyword &&
    !state?.searchLibraryData?.Videos?.length &&
    !state?.searchLibraryData?.Bulletins?.length &&
    !state?.searchLibraryData?.Documents?.length &&
    !state?.searchLibraryData?.Podcast?.length &&
    !state?.searchLibraryData?.Books?.length &&
    !state?.searchLibraryData?.Recommended?.length;

  return (
    <>
      <div className="flex justify-between items-center mt-6 md:mt-16 space-x-4 xl:px-32 lg:px-8 md:px-8 px-5">
        <div></div>
        <div className="flex gap-4 w-max">
          <Search onSearchClick={onSearchClick} onhandleSearch={onhandleSearch} />
        </div>
      </div>
      {loading ? (
        <PageLoader />
      ) : (
        <>
          {isEmpty ? (
            <div className="flex justify-center items-center h-[calc(100vh-32rem)] w-full text-2xl">
              <span dangerouslySetInnerHTML={{ __html: (t('Library.noDetailsFound')) }} />
            </div>
          ) : (
            <>
              {
                state?.libraryData?.Recommended?.length > 0 && (
                  <RecommendedSection addBookMark={addBookMark}
                    deleteBookMark={deleteBookMark} keyword={keyword} />
                )
              }

              {sections?.map(section =>
                renderSection(
                  section,
                  state,
                  keyword,
                  addBookMark,
                  deleteBookMark,
                  librarySectionsIds,
                  tagsData
                )
              )}

              {!keyword ? <DailyReminderSection /> : ""}
            </>
          )}
        </>
      )}
    </>
  );
};

export default LibrarySubSections;
